import * as actionTypes from './_actionTypes';
// import { updateObject, lookupMessageKey } from '../utility';

const initialState = {
	adalProfile: null,
	bulkSelections: [],
	navStack: [],
	actionCart: {
		items: [],
	},
	modifyQueue: {
		items: [],
		currentIndex: 0,
		isLoading: false,
	},
	processedRequests: {
		items: [],
	},
};

/**
 * addBulkSelection
 * add a new member to the list of those to be updated in bulk
 * @param {*} state - copy of the existing state object
 * @param {*} action - object containing `selection` representing the new member to add to the list
 */
const addBulkSelection = (state, action) => {
	const newState = { ...state };
	if (
		!newState.bulkSelections.some(
			selection => selection.esoFiId === action.selection.esoFiId
		)
	) {
		newState.bulkSelections.push(action.selection);
	}
	return newState;
};

/**
 * removeBulkSelection
 * add a new member to the list of those to be updated in bulk
 * @param {*} state - copy of the existing state object
 * @param {*} action - object containing `selection` representing the new member to add to the list
 */
const removeBulkSelection = (state, action) => {
	const newState = { ...state };
	newState.bulkSelections = newState.bulkSelections.filter(
		obj => obj.esoFiId !== action.selection.esoFiId
	);
	return newState;
};

/**
 * resetBulkSelection
 * add a new member to the list of those to be updated in bulk
 * @param {*} state - copy of the existing state object
 * @param {*} action - object containing `selection` representing the new member to add to the list
 */
const resetBulkSelection = (state, action) => {
	const newState = { ...state };
	newState.bulkSelections = [];
	return newState;
};

/**
 * setAdalProfile
 * set the profile for the currently logged in user
 * @param {*} state- copy of the existing state object
 * @param {*} action - object containing `profile` representing the `profile` of the currently logged in user (from ADAL)
 */
const setAdalProfile = (state, action) => {
	const newState = { ...state };
	newState.adalProfile = { ...action.profile };
	return newState;
};

const setLoggedInUser = (state, action) => {
	const newState = { ...state };
	newState.adalProfile.loggedInUser = action.user;
	return newState;
};

/**
 * set the impersonation details for the currently logged in user
 * @param {*} state - copy of existing state object
 * @param {*} action - object containing `impersonator` account details
 */
const setImpersonator = (state, action) => {
	const newState = { ...state };
	newState.adalProfile.impersonator = action.impersonator;
	return newState;
};

const updateNavigation = (state, action) => {
	return state;
};

/**
 * setActionCartItems
 * Replace the entire UAR action cart items list
 */
const setActionCartItems = (state, action) => {
	const newState = { ...state };
	newState.actionCart = {
		...newState.actionCart,
		items: Array.isArray(action.items) ? action.items : [],
	};
	return newState;
};

/**
 * addCartAction
 * Add or toggle a UAR action cart item
 */
const addCartAction = (state, action) => {
	const newState = { ...state };
	const item = action.item;

	if (!item?.itemKey) {
		return newState;
	}

	const existingIndex = newState.actionCart.items.findIndex(
		currentItem => currentItem.itemKey === item.itemKey
	);

	if (existingIndex === -1) {
		newState.actionCart.items = [...newState.actionCart.items, item];
	} else {
		const existingItem = newState.actionCart.items[existingIndex];
		if (existingItem.action === item.action) {
			newState.actionCart.items = newState.actionCart.items.filter(
				currentItem => currentItem.itemKey !== item.itemKey
			);
		} else {
			const updatedItems = [...newState.actionCart.items];
			updatedItems[existingIndex] = { ...existingItem, ...item };
			newState.actionCart.items = updatedItems;
		}
	}

	return newState;
};

/**
 * removeCartAction
 * Remove a single UAR action cart item by key
 */
const removeCartAction = (state, action) => {
	const newState = { ...state };
	newState.actionCart.items = newState.actionCart.items.filter(
		item => item.itemKey !== action.itemKey
	);
	return newState;
};

/**
 * clearActionCart
 * Clear all UAR action cart items
 */
const clearActionCart = (state, action) => {
	const newState = { ...state };
	newState.actionCart.items = [];
	return newState;
};

/**
 * setModifyQueue
 * Replace the entire UAR modify queue
 */
const setModifyQueue = (state, action) => {
	const newState = { ...state };
	newState.modifyQueue = {
		items: Array.isArray(action.items) ? action.items : [],
		currentIndex: 0,
		isLoading: false,
	};
	return newState;
};

/**
 * advanceQueue
 * Move to the next item in the UAR modify queue
 */
const advanceQueue = (state, action) => {
	const newState = { ...state };
	const nextIndex = newState.modifyQueue.currentIndex + 1;
	if (nextIndex < newState.modifyQueue.items.length) {
		newState.modifyQueue.currentIndex = nextIndex;
	}
	return newState;
};

/**
 * resetQueue
 * Clear the entire UAR modify queue and reset current index
 */
const resetQueue = (state, action) => {
	const newState = { ...state };
	newState.modifyQueue = {
		items: [],
		currentIndex: 0,
		isLoading: false,
	};
	return newState;
};

/**
 * setProcessedRequests
 * Replace the entire UAR processed requests list
 */
const setProcessedRequests = (state, action) => {
	const newState = { ...state };
	newState.processedRequests = {
		items: Array.isArray(action.items) ? action.items : [],
	};
	return newState;
};

/**
 * clearProcessedRequests
 * Clear all UAR processed requests
 */
const clearProcessedRequests = (state, action) => {
	const newState = { ...state };
	newState.processedRequests.items = [];
	return newState;
};

const reducer = (state = initialState, action) => {
	switch (action.type) {
		case actionTypes.ADD_BULK_SELECTION:
			return addBulkSelection(state, action);
		case actionTypes.REMOVE_BULK_SELECTION:
			return removeBulkSelection(state, action);
		case actionTypes.RESET_BULK_SELECTION:
			return resetBulkSelection(state, action);
		case actionTypes.SET_PROFILE:
			return setAdalProfile(state, action);
		case actionTypes.SET_IMPERSONATOR:
			return setImpersonator(state, action);
		case actionTypes.SET_LOGGED_IN_USER:
			return setLoggedInUser(state, action);
		case actionTypes.UPDATE_NAVIGATION:
			return updateNavigation(state, action);
		case actionTypes.SET_ACTION_CART_ITEMS:
			return setActionCartItems(state, action);
		case actionTypes.ADD_CART_ACTION:
			return addCartAction(state, action);
		case actionTypes.REMOVE_CART_ACTION:
			return removeCartAction(state, action);
		case actionTypes.CLEAR_ACTION_CART:
			return clearActionCart(state, action);
		case actionTypes.SET_MODIFY_QUEUE:
			return setModifyQueue(state, action);
		case actionTypes.ADVANCE_QUEUE:
			return advanceQueue(state, action);
		case actionTypes.RESET_QUEUE:
			return resetQueue(state, action);
		case actionTypes.SET_PROCESSED_REQUESTS:
			return setProcessedRequests(state, action);
		case actionTypes.CLEAR_PROCESSED_REQUESTS:
			return clearProcessedRequests(state, action);
		default:
			return state;
	}
};

export default reducer;
